import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'

interface ExpenseCategory {
  id: number
  code: string
  label: string
  accountCode: string
  defaultCostCenterId: number | null
  deductPercent: number
  ordinal: number
  active: boolean
  defaultCostCenter: { id: number; code: string; name: string } | null
}

interface CostCenter {
  id: number
  code: string
  name: string
}

const Schema = z.object({
  code: z
    .string()
    .min(1, 'Cod obligatoriu')
    .regex(/^[a-z0-9-]+$/, 'Doar litere mici, cifre, liniuțe'),
  label: z.string().min(1, 'Denumire obligatorie'),
  accountCode: z.string().min(1, 'Cont contabil obligatoriu'),
  defaultCostCenterId: z.preprocess(
    (v) => (v === '' || v == null ? undefined : v),
    z.coerce.number().int().positive().optional(),
  ),
  deductPercent: z.coerce.number().int().min(0).max(100),
  ordinal: z.coerce.number().int().nonnegative(),
  active: z.boolean().default(true),
})

type Input = z.infer<typeof Schema>

export function SetariCategoriiCheltuieliPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.expense_categories:write')
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ExpenseCategory | null>(null)

  const query = useQuery<{ categories: ExpenseCategory[] }>({
    queryKey: ['expense-categories'],
    queryFn: async () =>
      (await api.get<{ categories: ExpenseCategory[] }>('/expense-categories')).data,
  })

  const ccQuery = useQuery<{ items: CostCenter[] }>({
    queryKey: ['cost-centers'],
    queryFn: async () => (await api.get<{ items: CostCenter[] }>('/settings/cost-centers')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Input) => (await api.post('/expense-categories', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] })
      toast.success('Categorie adăugată')
      setCreateOpen(false)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Input }) =>
      (await api.patch(`/expense-categories/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] })
      toast.success('Actualizat')
      setEditTarget(null)
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/expense-categories/${id}`)).data,
    onSuccess: (data: { deactivated?: boolean }) => {
      queryClient.invalidateQueries({ queryKey: ['expense-categories'] })
      toast.success(data?.deactivated ? 'Dezactivată (e folosită pe linii)' : 'Ștearsă')
    },
    onError: (err: unknown) => toast.error(extractError(err)),
  })

  const categories = useMemo(() => query.data?.categories ?? [], [query.data])
  const costCenters = useMemo(() => ccQuery.data?.items ?? [], [ccQuery.data])
  const nextOrdinal = useMemo(
    () => Math.max(0, ...categories.map((c) => c.ordinal)) + 1,
    [categories],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Categorii cheltuieli</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Adaugă categorie</Button>}
      </div>

      <Card>
        <p className="mb-3 text-xs text-b4l-text-muted">
          Categoriile leagă cheltuielile de conturile contabile din EA (cu analitic). La completarea
          liniilor de decont, categoria completează automat contul și centrul de cost.
        </p>
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : categories.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Nicio categorie definită.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">#</th>
                <th className="py-2 pr-3 font-medium">Denumire</th>
                <th className="py-2 pr-3 font-medium">Cod</th>
                <th className="py-2 pr-3 font-medium">Cont contabil</th>
                <th className="py-2 pr-3 font-medium">Centru implicit</th>
                <th className="py-2 pr-3 font-medium">Deductibil</th>
                <th className="py-2 pr-3 font-medium">Stare</th>
                {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 text-b4l-text-muted">{c.ordinal}</td>
                  <td className="py-2 pr-3 font-medium">{c.label}</td>
                  <td className="py-2 pr-3 font-mono text-xs text-b4l-text-muted">{c.code}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{c.accountCode}</td>
                  <td className="py-2 pr-3 text-xs">
                    {c.defaultCostCenter ? c.defaultCostCenter.code : '—'}
                  </td>
                  <td className="py-2 pr-3 tabular-nums">{c.deductPercent}%</td>
                  <td className="py-2 pr-3">
                    {c.active ? (
                      <span className="rounded bg-green-100 px-2 py-0.5 text-[10px] text-green-700">
                        activ
                      </span>
                    ) : (
                      <span className="rounded bg-b4l-bg px-2 py-0.5 text-[10px] text-b4l-text-muted">
                        inactiv
                      </span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => setEditTarget(c)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Editează
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Ștergi categoria ${c.label}?`)) deleteMutation.mutate(c.id)
                          }}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Șterge
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {createOpen && (
        <CategoryFormModal
          title="Adaugă categorie"
          submitLabel="Creează"
          costCenters={costCenters}
          defaultOrdinal={nextOrdinal}
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {editTarget && (
        <CategoryFormModal
          title={`Editează ${editTarget.label}`}
          submitLabel="Salvează"
          costCenters={costCenters}
          defaultOrdinal={editTarget.ordinal}
          initial={{
            code: editTarget.code,
            label: editTarget.label,
            accountCode: editTarget.accountCode,
            defaultCostCenterId: editTarget.defaultCostCenterId ?? undefined,
            deductPercent: editTarget.deductPercent,
            ordinal: editTarget.ordinal,
            active: editTarget.active,
          }}
          lockCode
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editTarget.id, data })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

interface CategoryFormModalProps {
  title: string
  submitLabel: string
  costCenters: CostCenter[]
  defaultOrdinal: number
  initial?: Input
  lockCode?: boolean
  onClose: () => void
  onSubmit: (data: Input) => void
  submitting: boolean
}

function CategoryFormModal({
  title,
  submitLabel,
  costCenters,
  defaultOrdinal,
  initial,
  lockCode,
  onClose,
  onSubmit,
  submitting,
}: CategoryFormModalProps) {
  const form = useForm<Input>({
    resolver: zodResolver(Schema),
    defaultValues: initial ?? {
      code: '',
      label: '',
      accountCode: '',
      deductPercent: 100,
      ordinal: defaultOrdinal,
      active: true,
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => form.handleSubmit(onSubmit)()} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form className="space-y-3" noValidate onSubmit={(e) => e.preventDefault()}>
        <Field
          label="Denumire"
          {...form.register('label')}
          error={form.formState.errors.label?.message}
        />
        <Field
          label="Cod"
          hint="kebab-case, ex: mese-protocol-prezentare"
          {...form.register('code')}
          error={form.formState.errors.code?.message}
          disabled={lockCode}
        />
        <Field
          label="Cont contabil"
          hint="Cont EA cu analitic, ex: 6232.MK139, 6022.ded, 628.01"
          {...form.register('accountCode')}
          error={form.formState.errors.accountCode?.message}
        />
        <div>
          <label className="mb-1 block text-sm font-medium">Centru de cost implicit</label>
          <select
            {...form.register('defaultCostCenterId')}
            className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
          >
            <option value="">— niciunul —</option>
            {costCenters.map((cc) => (
              <option key={cc.id} value={cc.id}>
                {cc.code} — {cc.name}
              </option>
            ))}
          </select>
        </div>
        <Field
          label="Procent deductibil"
          type="number"
          hint="100 standard; 50 doar dacă firma aplică restricția la înregistrare"
          {...form.register('deductPercent')}
          error={form.formState.errors.deductPercent?.message}
        />
        <Field
          label="Ordine afișare"
          type="number"
          {...form.register('ordinal')}
          error={form.formState.errors.ordinal?.message}
        />
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" {...form.register('active')} />
          Activă
        </label>
      </form>
    </Modal>
  )
}

function extractError(err: unknown): string {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  )
}
